import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Wallet, Activity, TrendingUp, Shield, Gauge, MinusCircle } from "lucide-react";
import { api, fmtUSD, fmtPrice, fmtDate } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function ClientDashboard() {
  const [summary, setSummary] = useState(null);
  const [openTrades, setOpenTrades] = useState([]);
  const [marketOpen, setMarketOpen] = useState(true);

  const load = useCallback(async () => {
    const [m, t, mk] = await Promise.all([
      api.get("/client/me"),
      api.get("/client/trades", { params: { status: "open" } }),
      api.get("/market/rates"),
    ]);
    setSummary(m.data);
    setOpenTrades(t.data);
    if (mk.data.rates?.[0]) setMarketOpen(!!mk.data.rates[0].market_open);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load]);

  if (!summary) return <div className="text-muted-foreground" data-testid="client-dashboard-loading">Loading…</div>;

  const a = summary.account;

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-border">
        <Tile icon={Wallet}  label="Balance"      value={fmtUSD(a.balance)} testId="acc-balance" />
        <Tile icon={Activity} label="Equity"      value={fmtUSD(a.equity)} testId="acc-equity" highlight />
        <Tile icon={TrendingUp} label="Floating P&L" value={fmtUSD(a.floating_pnl, { signed: true })} valueClass={a.floating_pnl >= 0 ? "text-profit" : "text-loss"} testId="acc-floating-pnl" />
        <Tile icon={Shield}  label="Used Margin"  value={fmtUSD(a.used_margin)} testId="acc-used-margin" />
        <Tile icon={Gauge}   label="Free Margin"  value={fmtUSD(a.free_margin)} testId="acc-free-margin" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border">
        <div className="bg-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className={`label-eyebrow ${marketOpen ? "" : "text-warning"}`} data-testid="dashboard-market-label">
                {marketOpen ? "Live" : "Market Closed"}
              </p>
              <h3 className="text-base font-semibold flex items-center gap-2">
                Open Positions ({openTrades.length})
                {!marketOpen && (
                  <span className="text-[10px] font-semibold uppercase tracking-widest bg-warning/15 text-warning border border-warning/40 rounded px-2 py-0.5 flex items-center gap-1">
                    <MinusCircle className="w-3 h-3" /> Prices Frozen
                  </span>
                )}
              </h3>
            </div>
            <Link to="/client/trades" className="text-xs text-primary hover:underline" data-testid="view-all-trades">View all →</Link>
          </div>
          <div className="overflow-x-auto -mx-5">
            <Table>
              <TableHeader>
                <TableRow className="bg-surface-hover border-subtle">
                  <TableHead>Instrument</TableHead>
                  <TableHead>Side</TableHead>
                  <TableHead className="text-right">Lots</TableHead>
                  <TableHead className="text-right">Entry</TableHead>
                  <TableHead className="text-right">Current</TableHead>
                  <TableHead className="text-right">P&amp;L</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {openTrades.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No open positions</TableCell></TableRow>
                )}
                {openTrades.slice(0, 5).map((t) => (
                  <TableRow key={t.id} className="border-subtle" data-testid={`dash-open-${t.id}`}>
                    <TableCell className="font-semibold">{t.instrument}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.side === "buy" ? "bg-profit/15 text-profit" : "bg-loss/15 text-loss"}`}>
                        {t.side.toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono-num">{t.lots.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono-num">{fmtPrice(t.instrument, t.entry_price)}</TableCell>
                    <TableCell className="text-right font-mono-num">{fmtPrice(t.instrument, t.current_price)}</TableCell>
                    <TableCell className={`text-right font-mono-num font-semibold ${t.pnl >= 0 ? "text-profit" : "text-loss"}`}>
                      {fmtUSD(t.pnl, { signed: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="bg-card p-5">
          <p className="label-eyebrow mb-1">Account Health</p>
          <h3 className="text-base font-semibold mb-4">Margin Level</h3>
          <div className="font-mono-num text-4xl font-semibold mb-2" data-testid="margin-level">
            {a.used_margin > 0 ? `${a.margin_level.toFixed(1)}%` : "—"}
          </div>
          <div className="text-xs text-muted-foreground">
            {a.used_margin > 0
              ? a.margin_level > 200 ? "Healthy" : a.margin_level > 100 ? "Caution" : "At Risk"
              : "No open margin used"}
          </div>
          <div className="mt-6 space-y-3 text-sm">
            <Row k="Leverage" v={`1:${summary.user.leverage}`} />
            <Row k="Member since" v={fmtDate(summary.user.created_at)} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, valueClass = "", testId, highlight = false }) {
  return (
    <div className={`bg-card p-5 ${highlight ? "ring-1 ring-primary/30" : ""}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className={`font-mono-num text-2xl font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-mono-num">{v}</span>
    </div>
  );
}
